'use client';

import { useState, useEffect } from 'react';
import Script from 'next/script';
import ChatWidget from '@/components/widgets/ChatWidget';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import {
  hasAnalyticsConsent,
  setAnalyticsConsent,
  subscribeConsent,
} from '@/lib/analytics/consent';

/**
 * Marketing-only third-party pixels for public pages, gated behind consent.
 *
 * The cookie consent banner is rendered HERE, not in the root AnalyticsProvider —
 * this component mounts only in app/(public)/layout.tsx, so the banner appears on
 * public marketing pages only and never on the auth/(auth), (mfa), or authenticated
 * (private) app surfaces. Accepting writes to the shared consent store, which the
 * root AnalyticsProvider subscribes to, so GA4/PostHog/Datadog still flip on
 * reactively across the funnel.
 *
 * The remaining scripts are marketing-site-only and have no place in the app: the
 * visitor chat widget, MB Pounce + reb2b visitor identification, and the LinkedIn
 * Insight Tag. They react to the shared consent store so accepting in-session turns
 * these on without a reload.
 */
export default function PublicTrackingScripts() {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (hasAnalyticsConsent()) setAllowed(true);
    return subscribeConsent((c) => setAllowed(c === 'accepted'));
  }, []);

  return (
    <>
      {/* Cookie consent banner — public pages only (see component doc above). */}
      <CookieConsentBanner onConsent={setAnalyticsConsent} />

      {allowed && (
        <>
          {/* MB Chat Widget — visitor-facing chatbot */}
          <ChatWidget />

          {/* MB Pounce — website visitor identification */}
          <Script id='mb-pounce-config' strategy='afterInteractive'>
            {`
              window.mbPounceConfig = {
                vendorId: 2815,
                apiBaseUrl: '${process.env.NEXT_PUBLIC_HOST_URL || 'https://www.marketbetter.ai'}',
                enabled: true
              };
            `}
          </Script>
          <Script
            src='https://www.marketbetter.ai/mb-pounce.js'
            strategy='afterInteractive'
          />

          {/* reb2b — B2B visitor identification */}
          <Script id='reb2b-script' strategy='afterInteractive'>
            {`
              !function () {
                var reb2b = window.reb2b = window.reb2b || [];
                if (reb2b.invoked) return;
                reb2b.invoked = true;
                reb2b.methods = ["identify", "collect"];
                reb2b.factory = function (method) {
                  return function () {
                    var args = Array.prototype.slice.call(arguments);
                    args.unshift(method);
                    reb2b.push(args);
                    return reb2b;
                  };
                };
                for (var i = 0; i < reb2b.methods.length; i++) {
                  var key = reb2b.methods[i];
                  reb2b[key] = reb2b.factory(key);
                }
                reb2b.load = function (key) {
                  var script = document.createElement("script");
                  script.type = "text/javascript";
                  script.async = true;
                  script.src = "https://s3-us-west-2.amazonaws.com/b2bjsstore/b/" + key + "/reb2b.js.gz";
                  var first = document.getElementsByTagName("script")[0];
                  first.parentNode.insertBefore(script, first);
                };
                reb2b.SNIPPET_VERSION = "1.0.1";
                reb2b.load("0OV0VHL5QR6Z");
              }();
            `}
          </Script>

          {/* LinkedIn Insight Tag */}
          <Script id='linkedin-tracking' strategy='afterInteractive'>
            {`
              _linkedin_partner_id = "6750338";
              window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
              window._linkedin_data_partner_ids.push(_linkedin_partner_id);
              (function(l) {
                if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                window.lintrk.q=[]}
                var s = document.getElementsByTagName("script")[0];
                var b = document.createElement("script");
                b.type = "text/javascript"; b.async = true;
                b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                s.parentNode.insertBefore(b, s);
              })(window.lintrk);
            `}
          </Script>
        </>
      )}
    </>
  );
}
